import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ReactCountryFlag from "react-country-flag";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { ChevronLeftIcon, LockIcon } from "lucide-react";

import "./LiveChatAnalytics.scss";
import Message from "../../../i18n/Message";
import Spinner from "../../../assets/icons/Spinner";
import backend from "../../../backend/backend";
import { authSelectors } from "../../auth/store/authReducer";
import { linkToSubscription } from "../../../routes";
import { useTheme } from "../../../shared/theme/ThemeContext";
import ExampleMessageFeed from "../playground-widget/examples/ExampleMessageFeed";
import { UiModeContext } from "../playground-widget/context/UiModeContext";
import useSubscriptionGate from "../../shared/subscription/useSubscriptionGate";

const StatTile = ({ label, value }) => (
  <div className="stat_tile">
    <div className="stat_value">{value ?? 0}</div>
    <div className="stat_label">
      <Message id={label} />
    </div>
  </div>
);

const LiveChatAnalytics = ({ playground }) => {
  const nav = useNavigate();
  const user = useSelector(authSelectors.getUser);
  const { isDark } = useTheme();
  const [analytics, setAnalytics] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [openSession, setOpenSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Respect per-subscription overrides (e.g. an enterprise contract with Live Chat disabled),
  // not just the static plan mirror.
  const { can, isEnterprise, isReadOnly, contactSalesUrl } = useSubscriptionGate();
  const featureAvailable = can("liveChat");

  // Enterprise (top tier) has no higher self-serve plan: point them to contact instead of the
  // self-serve subscription page — except when read-only (lapsed), where renewing comes first.
  const upgradeCta = isEnterprise && !isReadOnly ? (
    <a
      className="btn bg-gradient-primary btn-sm"
      href={contactSalesUrl}
      target="_blank"
      rel="noreferrer"
    >
      <Message id="contact_sales_cta" />
    </a>
  ) : (
    <button
      className="btn bg-gradient-primary btn-sm"
      onClick={() => nav(linkToSubscription())}
    >
      <Message id="live_chat_upgrade_cta" />
    </button>
  );

  useEffect(() => {
    if (!featureAvailable) {
      setLoading(false);
      return;
    }
    Promise.all([
      backend.getLiveChatAnalytics(playground.uid),
      backend.getLiveChatSessions(playground.uid),
    ])
      .then(([a, s]) => {
        setAnalytics(a);
        setSessions(s);
      })
      .finally(() => setLoading(false));
  }, [playground.uid, featureAvailable]);

  const grid = isDark ? "#333" : "#e6e6e6";
  const axis = isDark ? "#8a94a6" : "#67748e";
  const lineColor = "#5b7fff";
  const barColor = "#37c8a3";

  // Group sessions by client-id ("user").
  const groups = useMemo(() => {
    const map = new Map();
    (sessions || []).forEach((s) => {
      const key = s.clientId || s.uid;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    });
    return Array.from(map.values());
  }, [sessions]);

  // Redux user may hydrate a tick after mount; don't flash the upgrade gate meanwhile.
  if (!user) {
    return (
      <div className="live_chat_analytics">
        <Spinner />
      </div>
    );
  }

  if (!featureAvailable) {
    return (
      <div className="live_chat_analytics upgrade_gate">
        <LockIcon size={28} color="#67748e" />
        <h3>
          <Message id="live_chat_upgrade_title" />
        </h3>
        <p>
          <Message id="live_chat_upgrade_text" />
        </p>
        {upgradeCta}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="live_chat_analytics">
        <Spinner />
      </div>
    );
  }

  if (openSession) {
    return (
      <div className="live_chat_analytics">
        <div className="breadcrumbs" onClick={() => setOpenSession(null)}>
          <ChevronLeftIcon size={16} />
          <Message id="live_chat_back_to_sessions" />
        </div>
        <UiModeContext.Provider value={{ uiMode: isDark ? "DARK" : "LIGHT" }}>
          <ExampleMessageFeed messages={openSession.messages} />
        </UiModeContext.Provider>
      </div>
    );
  }

  const usage = analytics?.usage || {};
  const overLimit = usage.messageLimit > 0 && usage.messageCount >= usage.messageLimit;

  return (
    <div className="live_chat_analytics">
      {overLimit && (
        <div className="limit_banner">
          <Message id="live_chat_limit_reached_owner" />
          {upgradeCta}
        </div>
      )}

      <div className="usage_meter">
        <Message id="live_chat_usage" />: {usage.messageCount ?? 0} /{" "}
        {usage.messageLimit ?? 0}
      </div>

      <div className="stat_row">
        <StatTile label="live_chat_stat_views" value={analytics?.totalViews} />
        <StatTile label="live_chat_stat_users" value={analytics?.totalUsers} />
        <StatTile
          label="live_chat_stat_countries"
          value={analytics?.totalCountries}
        />
        <StatTile
          label="live_chat_stat_messages"
          value={analytics?.totalMessages}
        />
        <StatTile
          label="live_chat_stat_tools"
          value={analytics?.totalToolUses}
        />
      </div>

      <div className="charts_row">
        <div className="chart_card">
          <div className="chart_title">
            <Message id="live_chat_chart_views" />
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={analytics?.viewsPerDay || []}>
              <CartesianGrid stroke={grid} vertical={false} />
              <XAxis dataKey="date" stroke={axis} fontSize={11} />
              <YAxis stroke={axis} fontSize={11} allowDecimals={false} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="count"
                stroke={lineColor}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="chart_card">
          <div className="chart_title">
            <Message id="live_chat_chart_messages" />
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={analytics?.messagesPerDay || []}>
              <CartesianGrid stroke={grid} vertical={false} />
              <XAxis dataKey="date" stroke={axis} fontSize={11} />
              <YAxis stroke={axis} fontSize={11} allowDecimals={false} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="count"
                stroke={barColor}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="chart_card">
          <div className="chart_title">
            <Message id="live_chat_chart_countries" />
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={analytics?.topCountries || []}>
              <CartesianGrid stroke={grid} vertical={false} />
              <XAxis dataKey="country" stroke={axis} fontSize={11} />
              <YAxis stroke={axis} fontSize={11} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill={barColor} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="sessions_list">
        <div className="sessions_title">
          <Message id="live_chat_sessions_title" />
        </div>
        {groups.length === 0 && (
          <div className="empty">
            <Message id="live_chat_no_sessions" />
          </div>
        )}
        {groups.map((group, gi) => {
          const head = group[0];
          return (
            <div className="user_group" key={gi}>
              <div className="user_group_header">
                {head.countryIsoCode && (
                  <ReactCountryFlag
                    countryCode={head.countryIsoCode}
                    svg
                    style={{ width: 18, height: 18 }}
                  />
                )}
                <span className="geo">
                  {[head.city, head.country].filter(Boolean).join(", ") || "—"}
                </span>
                <span className="ip">{head.anonymizedIp || "—"}</span>
                <span className="count">
                  {group.length} <Message id="live_chat_sessions_count" />
                </span>
              </div>
              {group.map((s) => (
                <div
                  className="session_row"
                  key={s.uid}
                  onClick={async () => {
                    const full = await backend.getLiveChatSession(
                      playground.uid,
                      s.uid
                    );
                    setOpenSession(full);
                  }}
                >
                  <span className="date">
                    {new Date(s.dateCreated).toLocaleString()}
                  </span>
                  <span className="msgs">
                    {s.messageCount} <Message id="live_chat_messages_label" />
                  </span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LiveChatAnalytics;
